import React, { ReactElement, ReactNode } from 'react';
import { isExternal, openNavLink, openWindow } from '../../utils/UrlUtils';

import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { Typography } from '@material-ui/core';

import { DisplayLinkSection } from '@alme/types/response/DisplayLinkSection';
import { AlmeResponseNext, AlmeResponseV5, AlmeResponseV6 } from '@alme/types/response/AlmeResponse';
import { DisplayLink } from '@alme/types/response/DisplayLink';
import { NavUrl } from '@alme/types/response/NavUrl';
import { UrlNavUrl } from '@alme/types/response/UrlNavUrl';
import { AlmeMarkdownResponseNext } from '../AlmeResponse/AlmeResponse';
import { UrlDisplayLink } from '@alme/types/response/UrlDisplayLink';
import { LinkNavigationTypes } from '@alme/types/response/LinkNavigationTypes';

import { FocusComponentProps } from '../../models/ComponentProps';

import AlmeStyledButton, { AlmeStyledButtonProps } from './AlmeStyledButton/AlmeStyledButton';
import { AlmeResponseClasses } from './AlmeResponse';

import { AlmeLiveChatOperatorResponseClasses } from '../LiveChat/AlmeLiveChatResponse/AlmeLiveChatOperatorResponse/AlmeLiveChatOperatorResponse';
import { AlmeLiveChatOperatorResponseConfig } from '../LiveChat/AlmeLiveChatResponse/AlmeLiveChatOperatorResponse/AlmeLiveChatOperatorResponseConfig';
import { AlmeIvaResponseConfig } from './AlmeIvaResponse/AlmeIvaResponseConfig';
import { AlmeApiUtils } from '../../api/AlmeApiUtils';
import { UnitDisplayLink } from '@alme/types/response/UnitDisplayLink';
import { InputDisplayLink } from '@alme/types/response/InputDisplayLink';
import defaultLogger from '../../Logger';
import { useIntl } from 'react-intl';
import marked from 'marked';
import parse, { DOMNode, domToReact, Element } from 'html-react-parser';
import { renderToStaticMarkup } from 'react-dom/server';
import {addGAEventTracker} from '../AlmeGoogleAnalytics/AlmeGoogleAnalyticsUtils';

/**
 * Checks for navigation type and renders link with corresponding properties.
 * 'MainPage' navType utilizes the isExternal function, which strips url addresses to reveal if the address
 * is outside of the current domain. If the urlAddress is external, the link will contain
 * the OpenInNewIcon.
 * @see isExternal
 */
function paintUrlLink(navType: LinkNavigationTypes, urlAddress: string, displayText: string, buttonProps: AlmeStyledButtonProps, onLinkClick: ((link: DisplayLink | NavUrl) => void), link: DisplayLink | NavUrl): ReactElement {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const intl = useIntl();
    const handleClick = (event: React.MouseEvent): void => {
        event.preventDefault();
        onLinkClick(link);
    };

    let icon: ReactNode = null;
    switch (navType) {
        case 'MainPage':
            if (isExternal(urlAddress)) {
                icon = <OpenInNewIcon color='inherit' titleAccess=
                    {intl.formatMessage({
                        id: "ivaResponse.openInNewIconTitle",
                        defaultMessage: 'Default Step Out Icon Title',
                        description: 'Step Out Icon element',
                    })} />;
            }
            return (<AlmeStyledButton size="small" onClick={handleClick} {...buttonProps} href={urlAddress} target="_self"
                endIcon={icon}>{displayText}</AlmeStyledButton>);
        case 'None':
            return (<AlmeStyledButton size="small" onClick={handleClick} {...buttonProps}>{displayText}</AlmeStyledButton>);
        case 'Popup':
        default:
            return (<AlmeStyledButton size="small" onClick={handleClick} {...buttonProps} href={urlAddress} target="_blank"
                endIcon={<OpenInNewIcon color="inherit" titleAccess=
                    {intl.formatMessage({
                        id: "ivaResponse.openInNewIconTitle",
                        defaultMessage: 'Default Step Out Icon Title',
                        description: 'Step Out Icon element',
                    })} />}>{displayText}</AlmeStyledButton>);
    }
}

/**
 * Handle alme navUrl clicked or auto nav to navigate the url in a new tab or the current window.
 * If clicked this should use the INavUrl.LinkNavigationType else use the INavUrl.AutoNavigationType.
 * @see [api/Conversation/converse](https://confluence.nextit.com/pages/viewpage.action?pageId=35688489)
 * @see [Processing the Agent Response to a Conversation Request](
 * https://confluence.nextit.com/display/AlmeTech/Processing+the+Agent+Response+to+a+Conversation+Request)
 */
export function onTriggerAutoNavUrl(link: NavUrl): void {
    const urlNavUrl = link as UrlNavUrl;

    // Auto Nav is not best practice and may be removed in future versions of Alme.
    // See  https://www.w3.org/TR/WCAG20/#context-changedef
    const navType = urlNavUrl.AutoNavigationType;

    switch (navType) {
        case 'MainPage':
            openWindow(urlNavUrl.UrlAddress, '_self');
            break;
        case 'None':
            break;
        case 'Popup':
        default:
            openWindow(urlNavUrl.UrlAddress, '_blank');
            break;
    }
}

/**
 * Returns the Alme DisplayLink as a material-ui Button JSX element.
 * @see [Material UI Button Component](https://material-ui.com/components/buttons/)
 * @returns Alme DisplayLink as a material-ui Button
 */
// eslint-disable-next-line react/display-name
const paintDisplayLink = (entryId: string, onLinkClick: ((link: DisplayLink | NavUrl) => void), classes: AlmeResponseClasses, sectionIndex: number, almeConfig: AlmeIvaResponseConfig, focusProps?: FocusComponentProps) => (link: DisplayLink, linkIndex: number): ReactElement => {
    const urlLink = link as UrlDisplayLink;
    const inlineKey=`${entryId}-link-${sectionIndex}-${linkIndex}`;

    if (link.Metadata["isInline"]) {
        return  <React.Fragment key={inlineKey}></React.Fragment>;
      }

    const buttonProps: AlmeStyledButtonProps = {
        className: classes.displayLink,
        color: almeConfig.ivaResponse.styles.displayLink.color,
        variant: almeConfig.ivaResponse.styles.displayLink.variant,
        fullWidth: true,
        key: inlineKey,
        ...focusProps,
    };

    switch (link.TargetType) {
        case 'Url':
            return paintUrlLink(urlLink.Metadata.NavigationType, urlLink.Metadata.UrlAddress, urlLink.DisplayText, buttonProps, onLinkClick, link);
        case 'None':
            return <AlmeStyledButton size="small" {...buttonProps}>{link.DisplayText}</ AlmeStyledButton>;
        case 'Input':
        case 'Unit':
        default:
            return <AlmeStyledButton size="small" {...buttonProps} onClick={(): void => { onLinkClick(link); }}>{link.DisplayText}</AlmeStyledButton>;
    }
};

export function paintSection(entryId: string, section: DisplayLinkSection, onLinkClick: ((link: DisplayLink | NavUrl) => void), classes: AlmeResponseClasses, almeConfig: AlmeIvaResponseConfig, focusProps?: FocusComponentProps): ReactElement[] {
    return section.Links.map(paintDisplayLink(entryId, onLinkClick, classes, section.SectionID, almeConfig, focusProps));
}

/**
 * Returns the Alme NavUrl as a material-ui Button JSX Element.
 * This function also triggers onTriggerNavUrl with clicked=false to support auto navigation.
 * @see [Material UI Button Component](https://material-ui.com/components/buttons/)
 * @see NavUrl
 * @returns Alme NavUrl as a material-ui Button
 */
export function paintNavUrl(entryId: string, navUrl: NavUrl | null, responseRevision: string, classes: AlmeResponseClasses | AlmeLiveChatOperatorResponseClasses, almeConfig: AlmeIvaResponseConfig | AlmeLiveChatOperatorResponseConfig, onLinkClick: ((link: DisplayLink | NavUrl) => void), focusProps?: FocusComponentProps, isLiveChat?: boolean,  disableButton?: boolean): ReactElement | null {
    const responseConfig = isLiveChat ? (almeConfig as AlmeLiveChatOperatorResponseConfig).liveChatOperatorResponse : (almeConfig as AlmeIvaResponseConfig).ivaResponse;
    if (navUrl?.DisplayText && (navUrl.UrlAddress || navUrl.LinkNavigationType === 'None')) {
        const shouldDisable = disableButton === false ? focusProps?.disabled : true;
        const buttonProps: AlmeStyledButtonProps = {
            id: `alme-iva-card-${responseRevision}-nav-url`,
            className: classes.navUrl,
            color: responseConfig.styles.navUrl.color,
            variant: responseConfig.styles.navUrl.variant,
            fullWidth: true,
            key: `${entryId}-url-${navUrl.UrlUID}-link-0`,
            ...focusProps,
            disabled: shouldDisable,
        };
        return paintUrlLink(navUrl.LinkNavigationType, navUrl.UrlAddress,
            navUrl.DisplayText, buttonProps, onLinkClick, navUrl);
    } else {
        return null;
    }
}

// Checks if display link is not inline and should be rendered with header
function isNotInlineLink(displayLink: DisplayLink): boolean {
    return !displayLink.Metadata["isInline"];
}
/**
 * Returns a h4 JSX Element that includes the text from first section HeaderText (i.e. section[0].HeaderText).
 * In this example we will only support the first section header text and not be creating multiple link sections.
 * @see DisplayLinkSection
 * @returns Section header JSX Element
 */
export function paintSectionHeader(sections: DisplayLinkSection[], className: string): ReactElement | null {
    if (sections?.length && sections[0].HeaderText && sections[0].Links?.length && sections[0].Links.some(isNotInlineLink)) {
        return (<Typography className={className} variant="subtitle2">{sections[0].HeaderText}</Typography>);
    } else {
        return null;
    }
}

/**
 * Returns the users masked input from the Alme response or null if there is no masked input.
 * @see AlmeResponseNext
 * @returns Users masked input or null
 */
export function getMaskedUserInput(response: AlmeResponseNext): string | undefined {
    return response.maskedInput?.trim();
}

/**
 * Returns the IVA response text from the Alme response.
 * @see AlmeResponseNext
 * @returns IVA response text from the Alme response
 */
export function getAgentResponseText(response: AlmeResponseNext): string | undefined {
    return response.text?.trim();
}

/**
 * Compares the attributes added to the placeholder elements with the display links passed in by the AlmeResponse.
 * This function will attempt to render and return the proper inline links.
 * @param placeHolderElement the element to be replaced by a displayLink
 * @param response post-markdown almeResponse
 * @param inlineClass styles to make buttons appear as <a> elements
 * @param onLinkClick function to be set to onclick for Input and Unit links
 * @returns the inlinelink element
 */
function replaceInlineLinkHtml(placeHolderElement: Element, response: AlmeMarkdownResponseNext, inlineClass: string, onLinkClick?: ((link: DisplayLink | NavUrl) => void)): JSX.Element {
    const displayText = placeHolderElement.attribs.title;
    const linkIndex = placeHolderElement.attribs["data-linkindex"];
    const sectionIdString = placeHolderElement.attribs["data-sectionid"];
    const sectionIdNumber: number = +sectionIdString;
    const defaultElement: JSX.Element = <p>{displayText}</p>;

    const displayLinks = response.displayLinkCollection?.Sections[sectionIdNumber].Links;
    if (displayLinks) {
        const link = displayLinks[+linkIndex];

        const handleClick = (event: React.MouseEvent): void => {
            event.preventDefault();
            if (onLinkClick) {
                onLinkClick(link);
            }

        };

        return <button id={`inline-link-${linkIndex}`} className={inlineClass} onClick={handleClick}>{link.DisplayText}{domToReact(placeHolderElement.children)}</button >;
    } else {
        return defaultElement;
    }
}

/**
 * This function is to be used as a parameter when parsing the HTML, it defines the replace function that is
 * used to insert inline links in place of the placeholder HTML elements.
 * @param response the post-markdown AlmeResponse
 * @param inlineClass styles that will be applied to buttons to make them look like <a> elements
 * @param onLinkClick will be utilized for Unit and Input links
 * @returns the replace function
 */
function getReplacingFunction(response: AlmeMarkdownResponseNext, inlineClass: string, onLinkClick?: ((link: DisplayLink | NavUrl) => void)) {
    return {
        replace: ((domNode: DOMNode) => {

            if (!domNode) {
                return;
            }
            const Element = domNode as Element;

            if (Element.type === "tag" && Element.attribs.id === "replace") {
                return replaceInlineLinkHtml(Element, response, inlineClass, onLinkClick);
            }
        }),
    };
}

function getHtmlAsArray(doc: string | JSX.Element | JSX.Element[]): JSX.Element[] | string[] {
    return Array.isArray(doc) ? doc : [doc] as JSX.Element[] | string[];
}

function replaceLinks(doc: string | JSX.Element | JSX.Element[], options: { replace: (domNode: DOMNode) => JSX.Element | undefined }) {
    const elements = getHtmlAsArray(doc);

    elements.forEach((element: JSX.Element | string) => {
        const children = ((element as JSX.Element)?.props as JSX.ElementChildrenAttribute)?.children;

        if (Array.isArray(children)) {
            children.forEach((child: ReactElement, index) => {
                const stringElement = renderToStaticMarkup(child);
                let replaced = parse(stringElement, options);
                const props = { key: stringElement };
                if (stringElement) {
                    if (typeof replaced !== 'string') {
                        replaced = React.cloneElement((replaced as ReactElement), props);
                    }
                    children[index] = (replaced as ReactElement);
                }
            });
        }
    });

    return doc;
}

export function getAgentResponse(response: AlmeMarkdownResponseNext, responseClass: string, inlineClass: string, onLinkClick?: ((link: DisplayLink | NavUrl) => void)): ReactElement | null {
    const sharedProps = {
        className: responseClass,
        variant: "body1" as const,
    };

    if (response.html) {
        const parsedHtml = parse(response.html);
        const options = getReplacingFunction(response, inlineClass, onLinkClick);
        const convertedHTML = replaceLinks(parsedHtml, options);

        return (
            <Typography component={'span'} {...sharedProps}>
                {convertedHTML}
            </Typography>
        );
    }

    const responseText = getAgentResponseText(response);
    if (!responseText) {
        return null;
    }

    return <Typography {...sharedProps} >{responseText}</Typography>;

}

/**
    * Handles Alme DisplayLink click.
    * Link Types:
    *  - Url link will navigate
    *  - Input link will submitAlmeInput with the 'Click' origin
    *  - Unit link will submit a unit request
    * @see [displayLinkCollection output property](https://confluence.nextit.com/pages/viewpage.action?pageId=35688489)
    */
export async function onDisplayLinkClick(link: DisplayLink | NavUrl, apiUtils: React.MutableRefObject<AlmeApiUtils>): Promise<void> {
    if ((link as DisplayLink).TargetType) {
        link = link as DisplayLink;
        addGAEventTracker(link.DisplayText);
        switch (link.TargetType) {
            case 'Unit':
                await apiUtils.current.submitUnitRequest(link as UnitDisplayLink);
                break;
            case 'Url':
                await apiUtils.current.submitReportingEvent('UrlClicked', link.Metadata.UrlAddress as string);
                openNavLink((link as UrlDisplayLink).Metadata.NavigationType, (link as UrlDisplayLink).Metadata.UrlAddress);
                break;
            case 'Input':
                await apiUtils.current.submitAlmeInput((link as InputDisplayLink).Metadata.InputText, 'Click', (link as InputDisplayLink).DisplayText);
                break;
            default:
                defaultLogger.warn('Unknown Link Type', link.TargetType);
                break;
        }
    }
    else {
        link = link as NavUrl;
        await apiUtils.current.submitReportingEvent('UrlClicked', link.Metadata?.UrlAddress as string);
        openNavLink(link.LinkNavigationType, link.UrlAddress);
    }
}

function getLinkSpec(sections: DisplayLinkSection[], matches: RegExpMatchArray): DisplayLink | null {
    const [displayText, sectionIndexStr, linkIndexStr] = matches.slice(1);

    // Convert from string to number or bail
    let linkIndex = parseInt(linkIndexStr, 10);
    if (isNaN(linkIndex)) {
        return null;
    }

    // Convert from string to number or bail
    // Also bail if index is -1 (i.e., not found)
    // Can't just .find() here because we might have to subtract
    let foundSectionIndex = parseInt(sectionIndexStr, 10);
    if (isNaN(foundSectionIndex)) {
        // eslint-disable-next-line array-callback-return
        foundSectionIndex = sections.findIndex((section) => {
            if (linkIndex < section.Links.length) {
                return true;
            }
            linkIndex -= section.Links.length;
        });

        if (foundSectionIndex < 0) {
            return null;
        }
    }

    const foundSection = sections[foundSectionIndex];
    if (!foundSection) {
        return null;
    }

    const link = foundSection.Links[linkIndex] || null;

    // Override text when specified
    if (link && displayText) {
        link.DisplayText = displayText;
    }
    return link;
}

function updateDisplayLinkSection(sections: DisplayLinkSection[], linkIndex: number): DisplayLinkSection[] {
    return sections.map((section) => {
        const displayLinks = section.Links;
        const link = displayLinks[linkIndex];
        link.Metadata = {
            ...link.Metadata,
            isInline: true,
        };
        section.Links = displayLinks;
        return section;
    }).filter((section) => {
        // remove sections without any links.
        return !!section.Links.length;
    });
}

function buildInlineLinkReplacer(responseText: string, matches: RegExpExecArray, link: UrlDisplayLink | UnitDisplayLink | InputDisplayLink): string {
    return responseText.replace(matches[0], `<span id="replace" title="${link.DisplayText}" data-sectionId="${link.SectionID}" data-linkIndex="${matches[3]}"></span>`);
}

export function MarkUp(inText: string): string {
    return inText ? marked(inText) : inText;
}

/**
 * Parse the HTML string for inline links syntax from FPML, if found replace the substring with
 * a temporary element that will later be replaced with the inline link.
 * @param response is the full AlmeResponse containing post-markdown HTML
 * @returns the response text with placeholder elements for inline links
 */
export function escapeAlmeDisplayLink(response: AlmeResponseV5 | AlmeResponseV6 | AlmeResponseNext): string {
    const inlineRegex = /\[([^\][]+)\]\((?:(\d{1,2}),)?(\d{1,2})?\)/ig;
    const sections = response.displayLinkCollection ? response.displayLinkCollection.Sections : [];
    let { text } = response;
    let matches: RegExpExecArray | null;
    // eslint-disable-next-line no-cond-assign
    while (matches = inlineRegex.exec(text)) {
        const link = getLinkSpec(sections, matches);
        if (link) {
            switch (link.TargetType) {
                case 'Url':
                    text = buildInlineLinkReplacer(text, matches, link as UrlDisplayLink);
                    break;
                case 'Unit':
                    text = buildInlineLinkReplacer(text, matches, link as UnitDisplayLink);
                    break;
                case 'Input':
                    text = buildInlineLinkReplacer(text, matches, link as InputDisplayLink);
                    break;
                case "None":
                    text = text.replace(matches[0], matches[1]);
                    break;
            }
            if (response.displayLinkCollection) {
                response.displayLinkCollection.Sections = updateDisplayLinkSection(response.displayLinkCollection.Sections, +matches[3]);
            }
        } else {
            text = text.replace(matches[0], matches[1]);
        }
    }
    return text;
}

/**
 * Wrapper function for converting alme responses to markdown
 * @param response alme response
 * @returns markdown response
 */
export function ConvertAlmeResponse(response: AlmeResponseNext): AlmeMarkdownResponseNext {
    const responseText = escapeAlmeDisplayLink(response);
    return {
        ...response,
        html: MarkUp(responseText),
    } as AlmeMarkdownResponseNext;
}

